import React from 'react';
import './LineChart.css';

// Generic SVG line/step chart. Each series has its own {x: Date, y: number} points;
// x/y domains are computed across all series so multiple series share one grid.
const LineChart = ({
    series,
    height = 220,
    width = 640,
    interpolation = 'linear',
    xFormatter = (d) => d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }),
    yFormatter = (v) => v.toLocaleString('ru-RU'),
    emptyText = 'Нет данных',
}) => {
    const padding = { top: 16, right: 16, bottom: 28, left: 44 };
    const innerW = width - padding.left - padding.right;
    const innerH = height - padding.top - padding.bottom;

    const allPoints = series.flatMap(s => s.data);
    if (allPoints.length === 0) {
        return <div className="line-chart line-chart--empty">{emptyText}</div>;
    }

    const xs = allPoints.map(p => p.x.getTime());
    const ys = allPoints.map(p => p.y);
    const xMin = Math.min(...xs);
    const xMax = Math.max(...xs);
    const yMax = Math.max(1, ...ys);

    const scaleX = (t) => (xMax === xMin ? innerW / 2 : ((t - xMin) / (xMax - xMin)) * innerW);
    const scaleY = (v) => innerH - (v / yMax) * innerH;

    const buildPath = (data) => {
        const sorted = [...data].sort((a, b) => a.x - b.x);
        if (sorted.length === 1) {
            const x = scaleX(sorted[0].x.getTime());
            const y = scaleY(sorted[0].y);
            return `M ${x} ${y} L ${x} ${y}`;
        }
        let d = '';
        sorted.forEach((p, i) => {
            const x = scaleX(p.x.getTime());
            const y = scaleY(p.y);
            if (i === 0) { d += `M ${x} ${y}`; return; }
            if (interpolation === 'step') {
                const prevY = scaleY(sorted[i - 1].y);
                d += ` L ${x} ${prevY} L ${x} ${y}`;
            } else {
                d += ` L ${x} ${y}`;
            }
        });
        return d;
    };

    const yTicks = [0, 0.5, 1].map(f => Math.round(yMax * f));
    const xAxisPoints = series[0].data;
    const xTickPoints = xAxisPoints.length <= 6
        ? xAxisPoints
        : [xAxisPoints[0], xAxisPoints[Math.floor(xAxisPoints.length / 2)], xAxisPoints[xAxisPoints.length - 1]];

    return (
        <div className="line-chart">
            <svg viewBox={`0 0 ${width} ${height}`} className="line-chart__svg">
                <g transform={`translate(${padding.left},${padding.top})`}>
                    {yTicks.map((t, i) => (
                        <g key={i}>
                            <line x1={0} x2={innerW} y1={scaleY(t)} y2={scaleY(t)} className="line-chart__grid" />
                            <text x={-8} y={scaleY(t)} className="line-chart__y-label" textAnchor="end" dominantBaseline="middle">{yFormatter(t)}</text>
                        </g>
                    ))}
                    {series.map(s => (
                        <path key={s.key} d={buildPath(s.data)} fill="none" stroke={s.color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                    ))}
                    {series.map(s => s.data.map((p, i) => (
                        <circle key={`${s.key}-${i}`} cx={scaleX(p.x.getTime())} cy={scaleY(p.y)} r={s.data.length > 20 ? 0 : 3.5} fill={s.color} />
                    )))}
                    <line x1={0} x2={innerW} y1={innerH} y2={innerH} className="line-chart__axis" />
                    {xTickPoints.map((p, i) => (
                        <text key={i} x={scaleX(p.x.getTime())} y={innerH + 20} className="line-chart__x-label" textAnchor="middle">{xFormatter(p.x)}</text>
                    ))}
                </g>
            </svg>
            {series.length > 1 && (
                <div className="line-chart__legend">
                    {series.map(s => (
                        <div key={s.key} className="line-chart__legend-item">
                            <span className="line-chart__legend-dot" style={{ background: s.color }} />
                            {s.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default LineChart;
